import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Theme, apply, store, stored, watchSystem } from "../theme";

/**
 * Drawn, not typed.
 *
 * "☀" and "☾" are emoji-presentation by default on most platforms, so a text
 * glyph arrives as a full-colour picture - an uncontrolled colour sitting in a
 * header the brand book governs, and one that ignores `currentColor` when the
 * button is selected. U+FE0E would ask for text presentation and is honoured
 * inconsistently. Paths are neither.
 */
function Icon({ name }: { name: Theme }) {
  const common = {
    viewBox: "0 0 16 16", width: 14, height: 14, "aria-hidden": true,
    fill: "none", stroke: "currentColor", strokeWidth: 1.4,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
  };
  if (name === "light") {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="3.1" />
        <path d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2L3.1 3.1" />
      </svg>
    );
  }
  if (name === "dark") {
    return (
      <svg {...common}>
        <path d="M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6a5.8 5.8 0 1 0 7 7z" />
      </svg>
    );
  }
  // auto: half lit, half not - the same circle as the other two, so the three
  // read as one family rather than three unrelated pictures.
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M8 2.6a5.4 5.4 0 0 1 0 10.8z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Values and their catalogue keys; the words are resolved where they are
 *  drawn, because a module constant is evaluated before a language is chosen. */
const OPTIONS: { value: Theme; label: "theme.light" | "theme.auto" | "theme.dark" }[] = [
  { value: "light", label: "theme.light" },
  { value: "auto", label: "theme.auto" },
  { value: "dark", label: "theme.dark" },
];

/**
 * Three states shown at once rather than one button that cycles.
 *
 * A cycling button cannot say what it will do next, and cannot show that
 * "auto" is a third thing rather than the absence of a choice - which is
 * exactly the distinction people need when the page looks wrong at dusk.
 */
export default function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(stored);

  useEffect(() => watchSystem(() => theme), [theme]);
  // The inline script in index.html has already applied the stored value, so
  // this only matters if that script did not run.
  useEffect(() => apply(theme), [theme]);

  return (
    <div
      role="radiogroup"
      aria-label={t("theme.label")}
      className="flex items-center rounded-md bg-brand-800/60 p-0.5 dark:bg-brand-950/60"
    >
      {OPTIONS.map((o) => {
        const on = theme === o.value;
        const label = t(o.label);
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={on}
            aria-label={label}
            title={label}
            className={`flex h-6 w-7 items-center justify-center rounded transition-colors ${
              on
                ? "bg-white text-brand-700"
                : "text-sky-200 hover:bg-brand-600 dark:hover:bg-brand-800"
            }`}
            onClick={() => {
              setTheme(o.value);
              store(o.value);
            }}
          >
            <Icon name={o.value} />
          </button>
        );
      })}
    </div>
  );
}
